import { useCallback, useEffect, useState } from "react";
import { getSections } from "../api/HttpApis";
import { nextPlacementKey } from "../component/Information/PublishingPayload";

// Matches the other callers in the CMS (HomePageBuilder, SubmissionDetail).
// This has to cover every page a tenant has, not just the first screenful:
// a placement pointing at a page outside the fetched set is indistinguishable
// from one pointing at a deleted page, and we reject the latter.
const SECTIONS_PAGE_SIZE = 500;

// Owns the Experience Builder pages behind the Publishing form's Category /
// Sub Category rows.
//
// This lives in the *form* rather than in <Publishing> because the Publishing
// accordion unmounts its body when collapsed (see Accordian.js) and starts
// collapsed. Fetching there meant a user who never opened the accordion had no
// sections loaded and no default row -- and was then blocked on save by a
// required field that was never rendered. Keeping it here makes both
// independent of whether the accordion has ever been opened.
export const useCategoryPlacements = ({ enabled = true, setPublishingData }) => {
  const [sections, setSections] = useState([]);
  // Distinguishes "this tenant has no pages yet" from "the request failed" --
  // a Category is mandatory, so a failed load otherwise leaves the user being
  // asked for a value the dropdown cannot offer.
  const [sectionsError, setSectionsError] = useState(false);
  const [sectionsLoading, setSectionsLoading] = useState(enabled);
  // False when the tenant has more pages than one request returned. A
  // truncated list must never be used to judge a placement "deleted".
  const [sectionsComplete, setSectionsComplete] = useState(false);
  // Whether the form has been told to show placement validation errors inline.
  const [showPlacementErrors, setShowPlacementErrors] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setSectionsLoading(false);
      return undefined;
    }
    let isMounted = true;
    setSectionsLoading(true);
    getSections(1, SECTIONS_PAGE_SIZE)
      .then((response) => {
        if (!isMounted) return;
        const payload = response?.data;
        const loaded = payload?.data?.rows || payload?.data || [];
        setSections(loaded);
        setSectionsError(false);
        // `total` is the tenant's full page count; when the response carries
        // it we can tell truncation exactly instead of guessing.
        const total = payload?.total ?? payload?.data?.total;
        setSectionsComplete(
          typeof total === "number" ? loaded.length >= total : loaded.length < SECTIONS_PAGE_SIZE
        );
      })
      .catch(() => {
        if (!isMounted) return;
        setSections([]);
        setSectionsError(true);
        setSectionsComplete(false);
      })
      .finally(() => {
        if (isMounted) setSectionsLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [enabled]);

  // Placements are optional, so nothing is seeded from here. <Publishing>
  // renders one blank row as an affordance when its accordion is open; an
  // empty row is ignored on save. ensureRow stays because a blocked save
  // (a half-filled row) still has to show the user the row it is complaining
  // about, even if the accordion was never opened.
  const ensureRow = useCallback(() => {
    if (!enabled) return;
    setPublishingData((prev) => {
      if (!prev) return prev;
      const rows = Array.isArray(prev.category_placements) ? prev.category_placements : [];
      if (rows.length) return prev;
      return {
        ...prev,
        category_placements: [{ _key: nextPlacementKey(), section_id: "", category_id: "" }],
      };
    });
  }, [enabled, setPublishingData]);

  return {
    sections,
    sectionsError,
    sectionsLoading,
    sectionsComplete,
    showPlacementErrors,
    setShowPlacementErrors,
    ensureRow,
  };
};

export default useCategoryPlacements;
